// "Search a city", as a panel under its word.
//
// There is one search field on the site. It used to be the landing's primary
// control; the landing's first question is now "which day did you love", and a
// reader who already knows where they are going gets there from the corner word
// on any view, or the landing's "Know where you're going?" button — both open
// this.
//
// The same panel is the "Compare Split with …" picker. The field is identical and
// so is the list; only what a pick DOES differs (open a briefing, or add a place
// to the comparison), and the sentence above the field says which. state.searchFor
// carries that, set by whoever opened the panel.

import { $ } from '../dom.ts';
import { state, store } from '../state.ts';
import { escapeHtml } from '../../lib/city-view.ts';
import { listNames } from '../../lib/compare-view.ts';
import type { CityHit } from '../../contracts/city.ts';
import type { Autocomplete } from './autocomplete.ts';

export interface SearchMenuDeps {
  wireAutocomplete: (input: HTMLInputElement, box: HTMLElement, onPick: (s: CityHit) => void, opts?: { clearOnPick?: boolean }) => Autocomplete;
  openCityHit: (s: CityHit) => void;
  addToComparison: (s: CityHit) => void;
}

let deps: SearchMenuDeps;
let ac: Autocomplete | null = null;

export function initSearchMenu(d: SearchMenuDeps): void {
  deps = d;
}

// Open the panel for one of its two jobs.
export function openSearch(forWhat: 'open' | 'compare' = 'open'): void {
  store.set({ searchFor: forWhat, menu: 'search' });
}

export function renderSearchMenu(): void {
  const box = $('search-menu');
  const open = state.menu === 'search';
  box.hidden = !open;
  const word = $('open-search');
  const compare = state.searchFor === 'compare';
  word.classList.toggle('open', open && !compare);
  word.setAttribute('aria-expanded', String(open && !compare));
  if (!open) return;

  const lead = $('search-lead');
  lead.hidden = !compare;
  if (compare) {
    const names = state.compare.places.map((p) => p.name);
    // "Compare Split with …" — the names already in hand, and the gap the next
    // pick fills. With nothing in hand yet, both ends are gaps.
    lead.innerHTML = `Compare <b>${names.length ? escapeHtml(listNames(names)) : '…'}</b> with <b>…</b>`;
  }
  $('search-submit').textContent = compare ? 'Add' : 'Show me the year';
  const input = $<HTMLInputElement>('search-input');
  input.placeholder = compare ? 'Another city — Palermo, Antalya…' : 'Search a city — Lisbon, Tokyo, Lima…';
  // After the frame the panel appears in, or the focus lands on a hidden node.
  requestAnimationFrame(() => input.focus());
  setTimeout(() => { if (state.menu === 'search') input.focus(); }, 0);
}

export function wireSearchMenu(): void {
  ac = deps.wireAutocomplete($<HTMLInputElement>('search-input'), $('suggestions'), (s) => {
    const compare = state.searchFor === 'compare';
    store.set({ menu: null });
    if (compare) deps.addToComparison(s);
    else deps.openCityHit(s);
  });
  $('search-submit').addEventListener('click', () => ac!.submit());
  $('open-search').addEventListener('click', (e) => {
    e.stopPropagation(); // the document closer would undo this click
    const openNow = state.menu === 'search' && state.searchFor === 'open';
    if (openNow) store.set({ menu: null });
    else openSearch('open');
  });
}
